import { Check, CloudOff, Loader2, RotateCcw } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";

const LABEL = { saving: "Saving…", saved: "All changes saved", error: "Couldn't save – will retry on next change", idle: "Changes save automatically", invalid: "Not saved – fill in the required fields" };

/** Debounced auto-save for an existing record. The first value seen after `ready` is the baseline; pending edits are flushed on leave. */
export function useAutoSave(data, save, { ready, enabled, delay = 900 }) {
  const baseline = useRef(null);
  const saveRef = useRef(save);
  const pending = useRef(null);
  const [status, setStatus] = useState("idle");
  saveRef.current = save;
  useEffect(() => {
    const flush = () => {
      if (pending.current) {
        const d = pending.current.data;
        pending.current = null;
        saveRef.current(d).catch(() => {});
      }
    };
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, []);
  useEffect(() => {
    if (!ready) {
      baseline.current = null;
      return undefined;
    }
    const json = JSON.stringify(data);
    if (baseline.current === null) {
      baseline.current = json;
      return undefined;
    }
    if (json === baseline.current) {
      pending.current = null;
      setStatus((s) => (s === "invalid" ? "idle" : s));
      return undefined;
    }
    if (!enabled) {
      pending.current = null;
      setStatus("invalid");
      return undefined;
    }
    pending.current = { data, json };
    const t = setTimeout(async () => {
      pending.current = null;
      setStatus("saving");
      try {
        await saveRef.current(data);
        baseline.current = json;
        setStatus("saved");
      } catch {
        setStatus("error");
      }
    }, delay);
    return () => clearTimeout(t);
  }, [data, ready, enabled, delay]);
  return { status };
}

/** Keeps an unsaved new-record form in this browser so nothing typed is lost. */
export function useDraft(key, data, restore, enabled) {
  const [loaded, setLoaded] = useState(!enabled);
  const [restored, setRestored] = useState(false);
  const restoreRef = useRef(restore);
  const empty = useRef(null);
  restoreRef.current = restore;
  useEffect(() => {
    if (!enabled) return;
    empty.current = JSON.stringify(data);
    const raw = localStorage.getItem(key);
    if (raw && raw !== empty.current) {
      try {
        restoreRef.current(JSON.parse(raw));
        setRestored(true);
      } catch {
        localStorage.removeItem(key);
      }
    }
    setLoaded(true);
  }, [key, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!enabled || !loaded) return undefined;
    const write = () => {
      const json = JSON.stringify(data);
      if (json === empty.current) localStorage.removeItem(key);
      else localStorage.setItem(key, json);
    };
    const t = setTimeout(write, 400);
    window.addEventListener("pagehide", write);
    return () => {
      clearTimeout(t);
      window.removeEventListener("pagehide", write);
      if (cleared.current) cleared.current = false;
      else write();
    };
  }, [key, data, enabled, loaded]);
  const cleared = useRef(false);
  const clear = useCallback(() => {
    setRestored(false);
    cleared.current = true;
    localStorage.removeItem(key);
  }, [key]);
  return { restored, clear };
}

export function SaveStatus({ status }) {
  const Icon = status === "saving" ? Loader2 : status === "error" || status === "invalid" ? CloudOff : Check;
  return (
    <span data-testid="autosave-status" className={`flex items-center gap-1.5 text-sm font-semibold ${status === "error" || status === "invalid" ? "text-wf-err" : "text-wf-mute"}`}>
      <Icon size={15} className={status === "saving" ? "animate-spin" : ""} />{LABEL[status]}
    </span>
  );
}

export function DraftBanner({ draft, onDiscard }) {
  if (!draft.restored) return null;
  return (
    <div data-testid="draft-restored-banner" className="mb-4 flex items-center gap-3 rounded-xl bg-wf-soft p-3 text-sm text-wf-softink">
      <RotateCcw size={16} /><span className="flex-1">Restored your unsaved draft.</span>
      <button data-testid="discard-draft-button" onClick={onDiscard} className="font-bold hover:underline">Discard</button>
    </div>
  );
}
